import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, DbProfil, DbOgretmen } from '@/lib/supabase';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: DbProfil | null;
  ogretmenRecord: DbOgretmen | null;
  role: 'yonetici' | 'ogretmen' | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ error: string | null; role: string | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<DbProfil | null>(null);
  const [ogretmenRecord, setOgretmenRecord] = useState<DbOgretmen | null>(null);
  const [role, setRole] = useState<'yonetici' | 'ogretmen' | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('egtpanel_profiller')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) {
      setProfile(null);
      setRole(null);
      setOgretmenRecord(null);
      return;
    }

    setProfile(data as DbProfil);
    setRole(data.rol as 'yonetici' | 'ogretmen');

    if (data.rol === 'ogretmen') {
      const { data: ogretmen } = await supabase
        .from('egtpanel_ogretmenler')
        .select('*')
        .eq('profil_id', userId)
        .maybeSingle();
      setOgretmenRecord(ogretmen as DbOgretmen | null);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        fetchProfile(s.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        fetchProfile(s.user.id);
      } else {
        setProfile(null);
        setRole(null);
        setOgretmenRecord(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ error: string | null; role: string | null }> => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { error: 'E-posta veya şifre hatalı. Lütfen tekrar deneyin.', role: null };
    }
    if (data.user) {
      await fetchProfile(data.user.id);
      const { data: profil } = await supabase
        .from('egtpanel_profiller')
        .select('rol')
        .eq('id', data.user.id)
        .maybeSingle();
      return { error: null, role: profil?.rol ?? null };
    }
    return { error: null, role: null };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRole(null);
    setOgretmenRecord(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, ogretmenRecord, role, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}